import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { JOB_STATUSES, PLAN_DEFINITIONS } from '@wrapforce/shared/constants';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useLocationStore, ALL_LOCATIONS } from '@/stores/location';
import { useJobs } from './api';
import { cn } from '@/lib/cn';
import { useLocaleFormatters } from '@/lib/format';

export function JobsPage() {
  const { t } = useTranslation('jobs');
  const { formatMoney } = useLocaleFormatters();
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const isInstaller = membership?.role === 'installer';
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<string | undefined>(searchParams.get('filter') === 'unpaid' ? 'unpaid' : undefined);
  const isUnpaidView = filter === 'unpaid';
  const activeLocationId = useLocationStore((s) => s.activeLocationId);
  const gateOn = membership ? PLAN_DEFINITIONS[membership.plan].gates.multiLocation : false;
  const locationId = gateOn && activeLocationId !== ALL_LOCATIONS ? activeLocationId : undefined;
  // "Unpaid" is orthogonal to job status, so it's filtered on the returned
  // balance rather than passed to the status query.
  const { data: allJobs = [] } = useJobs(workspaceId, { status: isUnpaidView ? undefined : filter, locationId });
  // A job only owes money once it has started - unscheduled and cancelled jobs
  // are not receivables, matching the dashboard's outstanding-balance rule.
  const jobs = isUnpaidView
    ? allJobs.filter((j) => j.balanceCents !== null && j.balanceCents > 0 && j.status !== 'unscheduled' && j.status !== 'cancelled')
    : allJobs;

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center gap-2 overflow-x-auto">
        <button onClick={() => setFilter(undefined)} className={cn('whitespace-nowrap px-3 py-1.5 text-xs font-semibold', !filter ? 'bg-ink text-surface' : 'text-muted')}>
          {t('filters.all')}
        </button>
        {!isInstaller ? (
          <button onClick={() => setFilter('unpaid')} className={cn('whitespace-nowrap px-3 py-1.5 text-xs font-semibold', isUnpaidView ? 'bg-danger text-white' : 'text-danger')}>
            {t('filters.unpaid')}
          </button>
        ) : null}
        {JOB_STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={cn('whitespace-nowrap px-3 py-1.5 text-xs font-semibold', filter === s ? 'bg-ink text-surface' : 'text-muted')}>
            {t(`status.job.${s}`)}
          </button>
        ))}
      </div>

      {/* Mobile: tappable card rows. Tables scroll poorly on phones, so the
          same rows render as a stacked card list below md. */}
      <div className="flex flex-col gap-2 md:hidden">
        {jobs.map((j) => (
          <Link
            key={j.id}
            to={`/jobs/${j.id}`}
            className="flex flex-col gap-1.5 rounded-[var(--radius-card)] border border-border bg-surface p-3.5 text-left"
          >
            <div className="flex items-start justify-between gap-2">
              <span className="font-mono text-[15px] font-semibold text-ink">J-{j.number}</span>
              <span className="shrink-0 bg-surface-2 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-muted">{t(`status.job.${j.status}`)}</span>
            </div>
            <div className="truncate text-xs text-ink">{j.customerName} · {j.vehicleLabel}</div>
            <div className="truncate text-xs text-muted">{j.serviceSummary}</div>
            <div className="mt-1 flex items-center justify-between border-t border-border pt-1.5">
              <span className="truncate text-xs text-muted">{j.installerNames.join(', ') || '-'}</span>
              {!isInstaller ? (
                j.status === 'cancelled' ? (
                  <span className="shrink-0 text-xs text-muted">{t('status.job.cancelled')}</span>
                ) : j.balanceCents !== null && j.balanceCents > 0 ? (
                  j.status === 'unscheduled' ? (
                    <span className="shrink-0 font-mono text-xs text-muted">{formatMoney(j.balanceCents)}</span>
                  ) : (
                    <span className="shrink-0 bg-danger/15 px-2 py-0.5 font-mono text-xs font-semibold text-danger">{formatMoney(j.balanceCents)}</span>
                  )
                ) : (
                  <span className="shrink-0 font-mono text-xs text-muted">{j.balanceCents !== null ? t('paidInFull') : '-'}</span>
                )
              ) : null}
            </div>
          </Link>
        ))}
        {jobs.length === 0 ? <div className="rounded-[var(--radius-card)] border border-border bg-surface px-4 py-10 text-center text-sm text-muted">{t('empty.noJobs')}</div> : null}
      </div>

      <div className="hidden overflow-hidden rounded-[var(--radius-card)] border border-border bg-surface md:block">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-surface-2 text-left text-xs font-bold uppercase tracking-wide text-muted">
            <tr>
              <th className="px-4 py-2.5">{t('table.job')}</th>
              <th className="px-4 py-2.5">{t('table.customerVehicle')}</th>
              <th className="px-4 py-2.5">{t('table.service')}</th>
              <th className="px-4 py-2.5">{t('table.installer')}</th>
              {!isInstaller ? <th className="px-4 py-2.5">{t('table.balance')}</th> : null}
              <th className="px-4 py-2.5">{t('table.status')}</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id} className="border-b border-border last:border-0 hover:bg-surface-2">
                <td className="px-4 py-3">
                  <Link to={`/jobs/${j.id}`} className="font-mono font-semibold text-ink hover:text-accent-ink">
                    J-{j.number}
                  </Link>
                </td>
                <td className="px-4 py-3 text-ink">{j.customerName} · {j.vehicleLabel}</td>
                <td className="px-4 py-3 text-muted">{j.serviceSummary}</td>
                <td className="px-4 py-3 text-muted">{j.installerNames.join(', ') || '-'}</td>
                {!isInstaller ? (
                  <td className="px-4 py-3 font-mono">
                    {j.status === 'cancelled' ? (
                      <span className="text-muted">{t('status.job.cancelled')}</span>
                    ) : j.balanceCents !== null && j.balanceCents > 0 ? (
                      j.status === 'unscheduled' ? (
                        <span className="text-muted">{formatMoney(j.balanceCents)}</span>
                      ) : (
                        <span className="bg-danger/15 px-2 py-0.5 font-semibold text-danger">{formatMoney(j.balanceCents)}</span>
                      )
                    ) : (
                      <span className="text-muted">{j.balanceCents !== null ? t('paidInFull') : '-'}</span>
                    )}
                  </td>
                ) : null}
                <td className="px-4 py-3">
                  <span className="bg-surface-2 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-muted">{t(`status.job.${j.status}`)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {jobs.length === 0 ? <div className="px-4 py-10 text-center text-sm text-muted">{t('empty.noJobs')}</div> : null}
      </div>
    </div>
  );
}
